import React, { useState } from 'react';
import { Dialog, DialogContent, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ListIcon from '@mui/icons-material/List';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { RootState, useAppDispatch, useAppSelector } from '../../../state/state';
import { closeModal } from '../../../state/ModalSlice/ModalSlice';
import { TextFieldComponent, DropdownComponent, RadioComponent, CheckboxComponent, LocationComponent, FileUploadComponent } from '../customFieldsFunctions';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../Config/firebase';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { selectEffectiveUID } from '../../../state/UserStatesV2/userSliceV2';

interface TabOption {
    id: string;
    label: string;
    icon: React.ReactNode;
}

const tabOptions: TabOption[] = [
    { id: 'text', label: 'Text', icon: <TextFieldsIcon className="w-5 h-5" /> },
    { id: 'options', label: 'Options', icon: <ListIcon className="w-5 h-5" /> },
    { id: 'location', label: 'Location', icon: <LocationOnIcon className="w-5 h-5" /> },
    { id: 'checkbox', label: 'Checkbox', icon: <CheckBoxIcon className="w-5 h-5" /> },
    { id: 'radio', label: 'Radio (Binary Choice)', icon: <RadioButtonCheckedIcon className="w-5 h-5" /> },
    { id: 'fileUpload', label: 'File Upload', icon: <CloudUploadIcon className="w-5 h-5" /> },
];

const AddEventCustomFieldsModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector((state: RootState) => state.ModalSlice.AddEventCustomFieldsModalOpen);
    const eventId = useAppSelector((state: RootState) => state.ArbitrarySlice.ForAddEventCustomFieldsModal.eventId);
    const serviceProviderId = useAppSelector(selectEffectiveUID); 
    const [selectedTab, setSelectedTab] = useState<string | null>(null);
    const [fieldData, setFieldData] = useState({
        fieldDescription: '',
        isRequired: false
    });

    const handleClose = () => {
        dispatch(closeModal("AddEventCustomFieldsModalOpen"));
        clearFieldData();
        setSelectedTab(null);
    };

    const handleTabClick = (tabId: string) => {
        setSelectedTab(tabId);
    };

    const handleFieldChange = (newData: { fieldDescription: string; isRequired: boolean }) => {
        setFieldData(newData);
    };

    const clearFieldData = () => {
        setFieldData({
            fieldDescription: '',
            isRequired: false
        });
    };

    const handleSubmit = async (field: { fieldDescription: string; isRequired: boolean; type: string; options?: string[] }) => {
        try {
            const eventRef = doc(db, 'ServiceProviders', serviceProviderId, 'Events', eventId);
            
            const newField = {
                ...field,
                id: uuidv4() 
            };
            
            await updateDoc(eventRef, {
                additionalFormFields: arrayUnion(newField)
            });
            toast.success('Field added successfully');
            handleClose();
       
        } catch (error) {
            console.error('Error saving custom field:', error);
            toast.error('Error saving Field');
        }
    };

    const renderSelectedComponent = () => {
        const props = {
            fieldDescription: fieldData.fieldDescription,
            isRequired: fieldData.isRequired,
            onFieldChange: handleFieldChange,
            onSubmit: handleSubmit
        };

        switch (selectedTab) {
            case 'text':
                return <TextFieldComponent {...props} />;
            case 'options':
                return <DropdownComponent {...props} />;
            case 'radio':
                return <RadioComponent {...props} />;
            case 'location':
                return <LocationComponent {...props} />; 
            case 'checkbox':
                return <CheckboxComponent {...props} />;
            case 'fileUpload':
                return <FileUploadComponent {...props} />;

            // Add other cases as needed
            default:
                return null;

        }
    };

    return (
        <Dialog
        sx={{
            padding: 0,
            '& .MuiPaper-root': {
                borderRadius: '10px',
            },
        }}
            maxWidth="md"
            open={isOpen}
            PaperProps={{
                component: 'div',
                className: 'relative',
            }}
        >
            <DialogContent sx={{ paddingX: "60px", paddingY: "50px" }} className="bg-white">
                <div className="w-[550px]">
                    <div className="header pb-[15px] space-y-2">
                        {selectedTab && (
                            <button
                                onClick={() => setSelectedTab(null)}
                                className="mb-2 flex items-center gap-1.5 text-gray-600 hover:text-gray-800 transition-colors duration-200 group"
                            >
                                <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-200" 
                                     fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                <span>Back</span>
                            </button>
                        )}
                        {!selectedTab && <h2 className="text-2xl font-semibold text-gray-800 transition-all duration-300">
                            {selectedTab ? 'Add Question' : 'Add Custom Questions'}
                        </h2>}
                        {!selectedTab && <p className="text-gray-500 font-medium">
                            Ask guests custom questions when they register.
                        </p>}
                    </div>

                    <div className="absolute right-4 top-4">
                        <IconButton 
                            onClick={handleClose}
                            className="hover:rotate-90 transition-transform duration-300"
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'rgba(0,0,0,0.04)',
                                }
                            }}
                        >
                            <CloseIcon className="text-gray-500 hover:text-gray-700" />
                        </IconButton>
                    </div>

                    {!selectedTab ? (
                        <div className="grid grid-cols-2 gap-4">
                            {tabOptions.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabClick(tab.id)}
                                    className="group flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                                >
                                    <div className="p-2.5 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors duration-300">
                                        {tab.icon}
                                    </div>
                                    <div className="text-left">
                                        <span className="block text-sm font-semibold text-gray-800">
                                            {tab.label}
                                        </span>
                                        <span className="text-xs text-gray-500 mt-0.5">
                                            Add {tab.label.toLowerCase()} field
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <Box className="min-h-[200px]">
                            {renderSelectedComponent()}
                        </Box>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AddEventCustomFieldsModal;
